import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  AreaChart,
  Area,
  Sankey,
} from 'recharts';
import { getEVDashboardData } from '../services/evDashboardApi';

/** Stratosphere theme blues (see frontend/src/styles/theme.css) — matches other app pages */
const EV = {
  primary: 'var(--color-primary)',
  light: 'var(--color-primary-light)',
  dark: 'var(--color-primary-dark)',
  p400: 'var(--color-primary-400)',
  p300: 'var(--color-primary-300)',
  p200: 'var(--color-primary-200)',
  p50: 'var(--color-primary-50)',
};

const defaultSankey = { nodes: [], links: [] };

const NODE_TYPE_LABEL = { segment: 'Segment', mineral: 'Mineral', country: 'Country' };

function getNodeIndex(nodes, key) {
  if (key == null) return -1;
  if (typeof key === 'number' && key >= 0 && key < nodes.length) return key;
  if (typeof key === 'object' && typeof key.index === 'number') return key.index;
  if (typeof key === 'object' && key.name != null)
    return nodes.findIndex((n) => n && n.name === key.name);
  return -1;
}

const SankeyTooltipContent = ({ active, payload, nodes = [] }) => {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0];
  const el = item.payload ?? item;
  const p = el?.payload ?? el;
  const srcIdx = getNodeIndex(nodes, p.source);
  const tgtIdx = getNodeIndex(nodes, p.target);
  const sourceNode = srcIdx >= 0 ? nodes[srcIdx] : null;
  const targetNode = tgtIdx >= 0 ? nodes[tgtIdx] : null;
  const sourceName =
    (typeof p.source === 'object' && p.source?.name) || sourceNode?.name || '—';
  const targetName =
    (typeof p.target === 'object' && p.target?.name) || targetNode?.name || '—';
  const sourceType = (typeof p.source === 'object' && p.source?.type) || sourceNode?.type;
  const targetType = (typeof p.target === 'object' && p.target?.type) || targetNode?.type;
  const sourceLabel = NODE_TYPE_LABEL[sourceType] || 'Source';
  const targetLabel = NODE_TYPE_LABEL[targetType] || 'Target';
  const value = p.value ?? item.value ?? 0;
  const metricLabel = p.metricLabel || 'Value';
  const targetEsg = typeof targetNode?.esg === 'number' ? targetNode.esg : null;
  return (
    <div
      className="bg-primary-theme text-white px-3 py-2 rounded-lg shadow-lg border text-sm"
      style={{
        zIndex: 9999,
        borderColor: 'color-mix(in srgb, var(--color-primary-light) 30%, transparent)',
      }}
    >
      <div className="font-semibold text-[color:var(--color-primary-200)]">Flow</div>
      <div><span className="text-[color:var(--color-primary-200)]">{sourceLabel}:</span> {sourceName}</div>
      <div><span className="text-[color:var(--color-primary-200)]">{targetLabel}:</span> {targetName}</div>
      <div><span className="text-[color:var(--color-primary-200)]">{metricLabel}:</span> {typeof value === 'number' ? value.toFixed(2) : value}</div>
      {targetEsg !== null && <div><span className="text-[color:var(--color-primary-200)]">ESG Score:</span> {targetEsg.toFixed(2)}</div>}
    </div>
  );
};

const getTooltipProps = () => ({
  isAnimationActive: false,
  wrapperStyle: { zIndex: 9999, pointerEvents: 'none', outline: 'none' },
  contentStyle: {
    borderRadius: '8px',
    border: '1px solid color-mix(in srgb, var(--color-primary-light) 35%, transparent)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
});

const DumbbellBar = (props) => {
  const { x, y, width, height, payload } = props;
  if (!payload) return null;
  const cy = y + height / 2;
  const x1 = x;
  const x2 = x + width;
  return (
    <g>
      <line x1={x1} y1={cy} x2={x2} y2={cy} stroke={EV.p400} strokeWidth={2} />
      <circle cx={x1} cy={cy} r={6} fill={EV.light} stroke="#fff" strokeWidth={1.5} />
      <circle cx={x2} cy={cy} r={6} fill="#59A14F" stroke="#fff" strokeWidth={1.5} />
    </g>
  );
};

const DumbbellTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div
      className="bg-primary-theme text-white px-3 py-2 rounded-lg shadow-lg border text-sm"
      style={{
        zIndex: 9999,
        borderColor: 'color-mix(in srgb, var(--color-primary-light) 30%, transparent)',
      }}
    >
      <div className="font-semibold text-[color:var(--color-primary-200)] mb-1">{row.state}</div>
      <div><span className="text-[color:var(--color-primary-200)]">Current:</span> {row.current}%</div>
      <div><span className="text-[color:var(--color-primary-200)]">Target:</span> {row.target}%</div>
      <div><span className="text-[color:var(--color-primary-200)]">Gap:</span> {(row.target - row.current).toFixed(1)}%</div>
    </div>
  );
};

const SafeBarTooltip = ({ active, payload, label, titleFormatter }) => {
  if (!active || !payload || !payload.length) return null;
  const title = titleFormatter ? titleFormatter(label, payload) : label;
  return (
    <div
      className="bg-primary-theme text-white px-3 py-2 rounded-lg shadow-lg border text-sm"
      style={{
        zIndex: 9999,
        borderColor: 'color-mix(in srgb, var(--color-primary-light) 30%, transparent)',
      }}
    >
      <div className="font-semibold text-[color:var(--color-primary-200)] mb-1">{title}</div>
      {payload.map((entry, idx) => {
        const val = Number(entry?.value);
        const valueText = Number.isFinite(val) ? val.toFixed(2) : String(entry?.value ?? '—');
        return (
          <div key={`${entry?.dataKey || 'v'}-${idx}`}>
            <span className="text-[color:var(--color-primary-200)]">{entry?.name || entry?.dataKey}:</span>{' '}
            {valueText}
          </div>
        );
      })}
    </div>
  );
};

const EVDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    evRank: [],
    batteryCost: [],
    co2Avoided: [],
    readinessScore: null,
    stockPerformance: [],
    evPublicFleet: [],
    stateEVGapData: [],
    policyStrength: [],
    esgScores: [],
    weightedRisk: [],
    sankeyData: defaultSankey,
  });

  useEffect(() => {
    let cancelled = false;
    getEVDashboardData()
      .then((payload) => {
        if (!cancelled) {
          setData({
            evRank: payload.evRank || [],
            batteryCost: payload.batteryCost || [],
            co2Avoided: payload.co2Avoided || [],
            readinessScore: payload.readinessScore ?? null,
            stockPerformance: payload.stockPerformance || [],
            evPublicFleet: payload.evPublicFleet || [],
            stateEVGapData: payload.stateEVGapData || [],
            policyStrength: payload.policyStrength || [],
            esgScores: payload.esgScores || [],
            weightedRisk: payload.weightedRisk || [],
            sankeyData: payload.sankeyData && payload.sankeyData.nodes ? payload.sankeyData : defaultSankey,
          });
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load dashboard');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const {
    evRank,
    batteryCost,
    co2Avoided,
    readinessScore,
    stockPerformance,
    evPublicFleet,
    stateEVGapData,
    policyStrength,
    esgScores,
    weightedRisk,
    sankeyData,
  } = data;
  const esgChartData = useMemo(
    () =>
      (esgScores || []).map((r) => {
        const clamp01 = (v) => {
          const n = Number(v);
          if (!Number.isFinite(n)) return 0;
          return Math.max(0, Math.min(1, n));
        };
        return {
          ...r,
          iso: String(r?.iso || '').toUpperCase(),
          E: clamp01(r?.E),
          S: clamp01(r?.S),
          G: clamp01(r?.G),
        };
      }),
    [esgScores],
  );
  const [activeCountryModal, setActiveCountryModal] = useState(null); // 'esg' | 'risk' | null
  const [selectedEsgCountryIsos, setSelectedEsgCountryIsos] = useState([]);
  const [selectedRiskCountryIsos, setSelectedRiskCountryIsos] = useState([]);
  const esgCountryOptions = useMemo(
    () =>
      esgChartData
        .map((r) => ({ iso: r.iso, country: r.country }))
        .filter((r) => r.iso)
        .sort((a, b) => (a.country || a.iso).localeCompare(b.country || b.iso)),
    [esgChartData],
  );
  const weightedRiskData = useMemo(
    () => (weightedRisk || []).map((r) => ({ ...r, iso: String(r?.iso || '').toUpperCase() })).filter((r) => r.iso),
    [weightedRisk],
  );
  const riskCountryOptions = useMemo(
    () =>
      weightedRiskData
        .map((r) => ({ iso: r.iso, country: r.country }))
        .filter((r) => r.iso)
        .sort((a, b) => (a.country || a.iso).localeCompare(b.country || b.iso)),
    [weightedRiskData],
  );
  useEffect(() => {
    if (!esgCountryOptions.length) {
      setSelectedEsgCountryIsos([]);
      return;
    }
    setSelectedEsgCountryIsos((prev) => {
      const allowed = new Set(esgCountryOptions.map((c) => c.iso));
      const kept = (prev || []).filter((iso) => allowed.has(iso));
      if (kept.length) return kept;
      return esgCountryOptions.slice(0, 10).map((c) => c.iso);
    });
  }, [esgCountryOptions]);
  useEffect(() => {
    if (!riskCountryOptions.length) {
      setSelectedRiskCountryIsos([]);
      return;
    }
    setSelectedRiskCountryIsos((prev) => {
      const allowed = new Set(riskCountryOptions.map((c) => c.iso));
      const kept = (prev || []).filter((iso) => allowed.has(iso));
      if (kept.length) return kept;
      return riskCountryOptions.slice(0, 10).map((c) => c.iso);
    });
  }, [riskCountryOptions]);
  const visibleEsgCountrySet = useMemo(() => new Set(selectedEsgCountryIsos), [selectedEsgCountryIsos]);
  const visibleRiskCountrySet = useMemo(() => new Set(selectedRiskCountryIsos), [selectedRiskCountryIsos]);
  const filteredEsgChartData = useMemo(
    () => esgChartData.filter((r) => visibleEsgCountrySet.has(r.iso)),
    [esgChartData, visibleEsgCountrySet],
  );
  const filteredWeightedRisk = useMemo(
    () => weightedRiskData.filter((r) => visibleRiskCountrySet.has(r.iso)),
    [weightedRiskData, visibleRiskCountrySet],
  );
  const esgChartKey = useMemo(
    () => `esg-${filteredEsgChartData.map((r) => r.iso).join('-')}-${filteredEsgChartData.length}`,
    [filteredEsgChartData],
  );

  // Normalize Sankey for Recharts: ensure nodes array (keep name + type) and links with numeric source/target/value
  const sankeyChartData = (() => {
    const raw = sankeyData?.nodes?.length ? sankeyData : defaultSankey;
    const nodes = (Array.isArray(raw.nodes) ? raw.nodes : []).map((n) =>
      typeof n === 'object' && n !== null ? { name: n.name, ...(n.type && { type: n.type }) } : { name: String(n) }
    );
    const rawLinks = Array.isArray(raw.links) ? raw.links : [];
    const links = rawLinks
      .map((l) => ({
        source: Number(l.source),
        target: Number(l.target),
        value: Math.max(0, Number(l.value) || 0),
      }))
      .filter((l) => !Number.isNaN(l.source) && !Number.isNaN(l.target) && l.value > 0);
    return { nodes, links };
  })();
  const hasSankeyData = sankeyChartData.nodes.length > 0 && sankeyChartData.links.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-secondary)] dark:bg-gray-900 text-primary dark:text-gray-100 font-sans flex items-center justify-center p-6">
        <p className="text-primary-light">Loading EV dashboard...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[var(--bg-secondary)] dark:bg-gray-900 text-primary dark:text-gray-100 font-sans flex items-center justify-center p-6">
        <p className="text-red-600">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)] dark:bg-gray-900 font-sans space-y-6 p-6">
      {/* Header */}
      <header className="space-y-1 mb-2">
        <h1 className="text-3xl font-bold text-primary dark:text-gray-100">
          India EV Intelligence — EV × Market × Public Transit × Policy × ESG
        </h1>
        <p className="text-sm text-primary opacity-80 dark:text-gray-300 dark:opacity-100">
          A unified view of EV adoption, listed OEM performance, public transport electrification,
          geoeconomic dependence, policy strength, and ESG risk.
        </p>
        <div className="pt-3 flex flex-wrap items-center gap-3">
          <Link
            to="/ev_crisis_simulator"
            className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold text-white bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] transition-colors"
          >
            Launch India EV Crisis Simulator
          </Link>
        </div>
      </header>

      {/* HERO: EV → Minerals → ESG Suppliers */}
      <section className="space-y-3">
        <h2 className="text-xl font-semibold text-primary-light">
          EV–Mineral–ESG Nexus — How India&apos;s EV Demand Pulls Global Minerals
        </h2>
        <Card className="shadow-sm rounded-2xl border border-[color:var(--color-primary-50)] bg-white overflow-visible">
          <CardHeader>
            <CardTitle className="text-primary-light text-base md:text-lg">
              Sankey — EV Segments → Critical Minerals → Supplier Countries
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-visible">
            <div className="w-full overflow-visible relative" style={{ zIndex: 1, minHeight: 320 }}>
              {hasSankeyData ? (
                <ResponsiveContainer width="100%" height={320}>
                  <Sankey
                    data={sankeyChartData}
                    isAnimationActive={false}
                    nodePadding={18}
                    margin={{ left: 0, right: 0, top: 10, bottom: 10 }}
                    link={{ stroke: EV.p400, strokeOpacity: 0.3 }}
                    node={{
                      fill: EV.light,
                      label: { fill: EV.primary, fontSize: 11 },
                    }}
                  >
                    <Tooltip
                      content={(p) => <SankeyTooltipContent {...p} nodes={sankeyChartData.nodes} />}
                      {...getTooltipProps()}
                      cursor={{ fill: 'transparent' }}
                    />
                  </Sankey>
                </ResponsiveContainer>
              ) : (
                <div
                  className="flex items-center justify-center text-primary opacity-70 dark:text-gray-400 dark:opacity-100 text-sm bg-[color:color-mix(in_srgb,var(--color-primary-50)_30%,transparent)] rounded-lg border border-[color:color-mix(in_srgb,var(--color-primary-light)_20%,transparent)]"
                  style={{ width: '100%', height: 320 }}
                >
                  No flow data available. EV segments, minerals, and supplier links will appear here once data is loaded.
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-primary opacity-60 dark:text-gray-400 dark:opacity-100">
              Left: India&apos;s EV segments (2W, 3W, 4W, buses). Middle: critical minerals. Right:
              supplier countries implicitly differentiated by ESG quality. Data sources: IEA Global EV Outlook (battery demand projections), USGS Mineral Commodity Summaries (mineral production), UN Comtrade (trade flows by mineral and country).
            </p>
          </CardContent>
        </Card>
      </section>

      {/* EV Layer: Bump chart + battery cost */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-primary-light">EV Layer — Adoption & Costs</h2>
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Bump chart: India rank vs peers */}
          <Card className="shadow-sm rounded-2xl border border-[color:var(--color-primary-50)] bg-white">
            <CardHeader>
              <CardTitle className="text-primary-light text-base md:text-lg">
                Bump Chart — India&apos;s EV Rank vs China, EU, US
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={evRank} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
                    <XAxis dataKey="year" stroke={EV.p400} tick={{ fontSize: 12 }} />
                    <YAxis
                      stroke={EV.p400}
                      tick={{ fontSize: 12 }}
                      domain={[1, 4]}
                      ticks={[1, 2, 3, 4]}
                      allowDecimals={false}
                      reversed
                      tickFormatter={(v) => `Rank ${v}`}
                    />
                    <Tooltip {...getTooltipProps()} formatter={(v) => `Rank ${v}`} />
                    <Line
                      type="monotone"
                      dataKey="India"
                      isAnimationActive={false}
                      stroke="#F28E2B"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                      name="India"
                    />
                    <Line
                      type="monotone"
                      dataKey="China"
                      isAnimationActive={false}
                      stroke="#59A14F"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name="China"
                    />
                    <Line
                      type="monotone"
                      dataKey="EU"
                      isAnimationActive={false}
                      stroke={EV.p200}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name="EU"
                    />
                    <Line
                      type="monotone"
                      dataKey="US"
                      isAnimationActive={false}
                      stroke={EV.p400}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name="US"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-primary opacity-60 dark:text-gray-400 dark:opacity-100">
                Relative rank of EV market share (1 = highest). Highlights India&apos;s position vs
                China, EU, and the US over time, not just absolute percentages. Data sources: IEA Global EV Outlook (country-level EV market share data), Vahan portal and industry reports for India.
              </p>
            </CardContent>
          </Card>

          {/* Battery cost vs global EV adoption */}
          <Card className="shadow-sm rounded-2xl border border-[color:var(--color-primary-50)] bg-white">
            <CardHeader>
              <CardTitle className="text-primary-light text-base md:text-lg">
                Battery Cost vs Global EV Adoption
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={batteryCost} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
                    <XAxis dataKey="year" stroke={EV.p400} tick={{ fontSize: 12 }} />
                    <YAxis stroke={EV.p400} tick={{ fontSize: 12 }} />
                    <Tooltip {...getTooltipProps()} />
                    <Area
                      type="monotone"
                      dataKey="cost"
                      isAnimationActive={false}
                      name="Battery $/kWh"
                      fill={EV.p300}
                      stroke={EV.light}
                      fillOpacity={0.3}
                    />
                    <Area
                      type="monotone"
                      dataKey="adoption"
                      isAnimationActive={false}
                      name="Global EV sales (M)"
                      fill={EV.p200}
                      stroke={EV.p400}
                      fillOpacity={0.4}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-primary opacity-60 dark:text-gray-400 dark:opacity-100">
                Data sources: BloombergNEF Battery Price Survey (battery cost trends), IEA Global EV Outlook (global EV sales and adoption rates).
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Market Layer */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-primary-light">
          Market Layer — Listed Indian EV & Auto Players
        </h2>
        <Card className="shadow-sm rounded-2xl border border-[color:var(--color-primary-50)] bg-white">
          <CardHeader>
            <CardTitle className="text-primary-light text-base md:text-lg">
              EV OEM Index vs Nifty Auto (2020 = 100)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stockPerformance} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
                  <XAxis dataKey="year" stroke={EV.p400} tick={{ fontSize: 12 }} />
                  <YAxis stroke={EV.p400} tick={{ fontSize: 12 }} />
                  <Tooltip {...getTooltipProps()} />
                  <Line
                    type="monotone"
                    dataKey="TataEV"
                    isAnimationActive={false}
                    name="Tata (EV index)"
                    stroke={EV.light}
                    strokeWidth={3}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="MahindraEV"
                    isAnimationActive={false}
                    name="Mahindra (EV index)"
                    stroke={EV.p300}
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="TVS"
                    isAnimationActive={false}
                    name="TVS"
                    stroke={EV.p200}
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="NiftyAuto"
                    isAnimationActive={false}
                    name="Nifty Auto index"
                    stroke={EV.p400}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-xs text-primary opacity-60 dark:text-gray-400 dark:opacity-100">
              Data sources: NSE/BSE historical stock prices for EV-exposed OEMs (Tata Motors, Mahindra & Mahindra, TVS Motor), Nifty Auto index (benchmark), normalized to 2020 = 100.
            </p>
          </CardContent>
        </Card>
      </section>

      {/* Public Transport & State Gaps */}
      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-primary-light">
          Public Transport Layer — EV Buses & 3-Wheelers
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Fleet evolution */}
          <Card className="shadow-sm rounded-2xl border border-[color:var(--color-primary-50)] bg-white">
            <CardHeader>
              <CardTitle className="text-primary-light text-base md:text-lg">
                EV Adoption in Public Fleets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={evPublicFleet} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
                    <XAxis dataKey="year" stroke={EV.p400} tick={{ fontSize: 12 }} />
                    <YAxis stroke={EV.p400} tick={{ fontSize: 12 }} />
                    <Tooltip {...getTooltipProps()} />
                    <Area
                      type="monotone"
                      dataKey="eBuses"
                      isAnimationActive={false}
                      name="e-bus stock (thousand)"
                      fill={EV.p300}
                      stroke={EV.light}
                      fillOpacity={0.3}
                    />
                    <Area
                      type="monotone"
                      dataKey="e3W"
                      isAnimationActive={false}
                      name="e-3W stock (million)"
                      fill={EV.p200}
                      stroke={EV.p400}
                      fillOpacity={0.4}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-primary opacity-60 dark:text-gray-400 dark:opacity-100">
                Data sources: Ministry of Housing and Urban Affairs (MoHUA) e-bus deployment data, state transport department reports, Society of Indian Automobile Manufacturers (SIAM) e-3W sales data.
              </p>
            </CardContent>
          </Card>

          {/* Dumbbell: state gaps */}
          <Card className="shadow-sm rounded-2xl border border-[color:var(--color-primary-50)] bg-white">
            <CardHeader>
              <CardTitle className="text-primary-light text-base md:text-lg">
                Dumbbell — Current vs Target EV Bus Share by State
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stateEVGapData.map((d) => ({
                      ...d,
                      _base: d.current,
                      _gap: Math.max(0, d.target - d.current),
                    }))}
                    layout="vertical"
                    margin={{ left: 30, right: 30, top: 10, bottom: 10 }}
                    barCategoryGap="30%"
                  >
                    <XAxis type="number" stroke={EV.p400} tick={{ fontSize: 12 }} domain={[0, 35]} unit="%" />
                    <YAxis type="category" dataKey="state" stroke={EV.p400} tick={{ fontSize: 12 }} width={100} />
                    <Tooltip content={<DumbbellTooltip />} {...getTooltipProps()} />
                    <Legend
                      payload={[
                        { value: 'Current %', type: 'circle', color: 'var(--color-primary-light)' },
                        { value: 'Target %', type: 'circle', color: '#59A14F' },
                      ]}
                    />
                    <Bar dataKey="_base" stackId="dumbbell" fill="transparent" isAnimationActive={false}>
                      {stateEVGapData.map((_, i) => (
                        <Cell key={i} fill="transparent" />
                      ))}
                    </Bar>
                    <Bar dataKey="_gap" stackId="dumbbell" shape={<DumbbellBar />} isAnimationActive={false}>
                      {stateEVGapData.map((_, i) => (
                        <Cell key={i} fill="transparent" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-primary opacity-60 dark:text-gray-400 dark:opacity-100">
                Visualises the ambition gap: current EV bus share vs target/benchmark per state. Data sources: Current shares from MoHUA and state transport departments; targets from state EV policies, FAME-II guidelines, and NITI Aayog state-level EV adoption roadmaps.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Policy & ESG Layer (compact) */}
      <section className="space-y-4 mb-10">
        <h2 className="text-xl font-semibold text-primary-light">Policy & ESG Layer</h2>
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Policy strength */}
          <Card className="shadow-sm rounded-2xl border border-[color:var(--color-primary-50)] bg-white">
            <CardHeader>
              <CardTitle className="text-primary-light text-base md:text-lg">
                EV Policy Strength — India vs Peers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={policyStrength} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
                    <XAxis dataKey="country" stroke={EV.p400} tick={{ fontSize: 12 }} />
                    <YAxis stroke={EV.p400} tick={{ fontSize: 12 }} />
                    <Tooltip {...getTooltipProps()} />
                    <Bar
                      dataKey="score"
                      isAnimationActive={false}
                      name="Policy score (0–100)"
                      fill={EV.p400}
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-primary opacity-60 dark:text-gray-400 dark:opacity-100">
                Data sources: IEA Global EV Policy Explorer (composite policy strength scores), incorporating fiscal incentives, industrial support measures, and regulatory frameworks across countries.
              </p>
            </CardContent>
          </Card>

          {/* ESG bars */}
          <Card className="shadow-sm rounded-2xl border border-[color:var(--color-primary-50)] bg-white">
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-primary-light text-base md:text-lg">
                  ESG Performance by Supplier Country
                </CardTitle>
                <button
                  type="button"
                  onClick={() => setActiveCountryModal('esg')}
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)]"
                >
                  Add Country
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedEsgCountryIsos.map((iso) => (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setSelectedEsgCountryIsos((prev) => prev.filter((x) => x !== iso))}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border border-[color:var(--color-primary-200)] text-primary"
                  >
                    {iso} <span aria-hidden="true">×</span>
                  </button>
                ))}
              </div>
              <div className="w-full h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart key={esgChartKey} data={filteredEsgChartData} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
                    <XAxis dataKey="iso" stroke={EV.p400} tick={{ fontSize: 12 }} />
                    <YAxis
                      domain={[0, 1]}
                      ticks={[0, 0.5, 1]}
                      stroke={EV.p400}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      {...getTooltipProps()}
                      content={<SafeBarTooltip titleFormatter={(label, payload) => {
                        const row = payload?.[0]?.payload;
                        return row?.country ? `${row.country} (${label})` : label;
                      }} />}
                    />
                    <Bar dataKey="E" fill={EV.light} name="Environmental" isAnimationActive={false} />
                    <Bar dataKey="S" fill={EV.p300} name="Social" isAnimationActive={false} />
                    <Bar dataKey="G" fill={EV.p200} name="Governance" isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-primary opacity-60 dark:text-gray-400 dark:opacity-100">
                ESG = (E + S + G) / 3, normalised 0–1. Data sources: Yale Environmental Performance Index (EPI) for Environmental scores, World Bank Worldwide Governance Indicators (WGI) for Governance, ILO and World Bank labour indicators for Social metrics.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Weighted ESG risk + impact */}
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="shadow-sm rounded-2xl border border-[color:var(--color-primary-50)] bg-white">
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-primary-light text-base md:text-lg">
                  Adjusted ESG Risk by Supplier Country
                </CardTitle>
                <button
                  type="button"
                  onClick={() => setActiveCountryModal('risk')}
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)]"
                >
                  Add Country
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedRiskCountryIsos.map((iso) => (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setSelectedRiskCountryIsos((prev) => prev.filter((x) => x !== iso))}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border border-[color:var(--color-primary-200)] text-primary"
                  >
                    {iso} <span aria-hidden="true">×</span>
                  </button>
                ))}
              </div>
              <div className="w-full h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={filteredWeightedRisk} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
                    <XAxis dataKey="iso" stroke={EV.p400} tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 1]} stroke={EV.p400} tick={{ fontSize: 12 }} />
                    <Tooltip
                      {...getTooltipProps()}
                      content={<SafeBarTooltip titleFormatter={(label, payload) => {
                        const row = payload?.[0]?.payload;
                        return row?.country ? `${row.country} (${label})` : label;
                      }} />}
                    />
                    <Bar dataKey="risk" fill={EV.p400} name="Adjusted ESG Risk" radius={[8, 8, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-primary opacity-60 dark:text-gray-400 dark:opacity-100">
                Adjusted ESG Risk = (1 - weighted_risk) x import share. Higher values signal
                more problematic sourcing.
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-sm rounded-2xl border border-[color:var(--color-primary-50)] bg-white">
            <CardHeader>
              <CardTitle className="text-primary-light text-base md:text-lg">
                Environmental Impact — CO₂ Avoided
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={co2Avoided} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
                    <XAxis dataKey="year" stroke={EV.p400} tick={{ fontSize: 12 }} />
                    <YAxis stroke={EV.p400} tick={{ fontSize: 12 }} />
                    <Tooltip {...getTooltipProps()} />
                    <Area
                      type="monotone"
                      dataKey="co2"
                      isAnimationActive={false}
                      name="MtCO₂ avoided"
                      fill={EV.p200}
                      stroke="#59A14F"
                      fillOpacity={0.4}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-primary opacity-60 dark:text-gray-400 dark:opacity-100">
                Data sources: IEA Global EV Outlook (annual CO₂ emissions avoided calculations), IEA Oil Market Report (oil displacement estimates), based on EV adoption rates and grid carbon intensity.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
      {activeCountryModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg max-h-[85vh] bg-white rounded-2xl border border-[color:var(--color-primary-50)] shadow-xl flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-[color:var(--color-primary-50)] flex items-center justify-between">
              <h3 className="text-sm font-semibold text-primary">Select countries to show</h3>
              <button type="button" className="text-primary-light" onClick={() => setActiveCountryModal(null)}>Close</button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto space-y-2">
              {(activeCountryModal === 'esg' ? esgCountryOptions : riskCountryOptions).map((c) => {
                const selected = activeCountryModal === 'esg' ? selectedEsgCountryIsos : selectedRiskCountryIsos;
                const checked = selected.includes(c.iso);
                return (
                  <label key={c.iso} className="flex items-center gap-2 text-sm text-primary">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        (activeCountryModal === 'esg'
                          ? setSelectedEsgCountryIsos((prev) => checked ? prev.filter((x) => x !== c.iso) : [...prev, c.iso])
                          : setSelectedRiskCountryIsos((prev) => checked ? prev.filter((x) => x !== c.iso) : [...prev, c.iso]))
                      }
                    />
                    <span>{c.country} ({c.iso})</span>
                  </label>
                );
              })}
            </div>
            <div className="px-4 py-3 border-t border-[color:var(--color-primary-50)] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    activeCountryModal === 'esg'
                      ? setSelectedEsgCountryIsos(esgCountryOptions.map((c) => c.iso))
                      : setSelectedRiskCountryIsos(riskCountryOptions.map((c) => c.iso))
                  }
                  className="px-3 py-1.5 rounded-full text-xs border border-[color:var(--color-primary-200)] text-primary"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() =>
                    activeCountryModal === 'esg'
                      ? setSelectedEsgCountryIsos(esgCountryOptions.slice(0, 10).map((c) => c.iso))
                      : setSelectedRiskCountryIsos(riskCountryOptions.slice(0, 10).map((c) => c.iso))
                  }
                  className="px-3 py-1.5 rounded-full text-xs border border-[color:var(--color-primary-200)] text-primary"
                >
                  Top 10
                </button>
              </div>
              <button
                type="button"
                onClick={() => setActiveCountryModal(null)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)]"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EVDashboard;

